import baseApi from "./RootBaseApi";
import {
  DiscountUsageFilters,
  DiscountReportsApiRaw,
  DiscountReportsNormalized,
} from "@/types/discount-reports.types";

export const DiscountReportsService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDiscountReports: builder.query<
      DiscountReportsNormalized,
      DiscountUsageFilters
    >({
      query: (params) => ({
        url: "admin/reports/subscriptions/discounts",
        method: "GET",
        params,
      }),
      transformResponse: (response: unknown): DiscountReportsNormalized => {
        const raw = (response as DiscountReportsApiRaw)?.data
          ? (response as DiscountReportsApiRaw).data
          : (response as unknown as DiscountReportsApiRaw["data"]);

        const summary = {
          total_users_applied: Number(raw?.summary?.total_users_applied ?? 0),
          total_orders_with_discount: Number(
            raw?.summary?.total_orders_with_discount ?? 0,
          ),
          total_discount_amount: Number(
            raw?.summary?.total_discount_amount ?? 0,
          ),
          revenue_before_discount: Number(
            raw?.summary?.revenue_before_discount ?? 0,
          ),
          revenue_after_discount: Number(
            raw?.summary?.revenue_after_discount ?? 0,
          ),
        };

        const rows = (raw?.rows || []).map((r) => ({
          subscription_type: String(r.subscription_type ?? ""),
          coupon_type: Number(r.coupon_type ?? 0),
          period: r.period ?? "",
          subscriptions_with_discount: Number(
            r.subscriptions_with_discount ?? 0,
          ),
          total_discount_amount: Number(r.total_discount_amount ?? 0),
        }));

        const usageItems = (raw?.usage?.items || []).map((it) => ({
          ...it,
          discount_amount: Number(it.discount_amount ?? 0),
          order_total_before: Number(it.order_total_before ?? 0),
          order_total_after: Number(it.order_total_after ?? 0),
        }));

        return {
          rows,
          summary,
          usage: {
            items: usageItems,
            totalItems: Number(raw?.usage?.totalItems ?? usageItems.length),
            currentPage: Number(raw?.usage?.currentPage ?? 1),
            pageLimit: Number(raw?.usage?.pageLimit ?? usageItems.length),
            totalPages: Number(raw?.usage?.totalPages ?? 1),
          },
        } as DiscountReportsNormalized;
      },
      providesTags: ["DiscountReports"],
      keepUnusedDataFor: 0,
    }),

    getDiscountReportsCsv: builder.query<
      Blob,
      DiscountUsageFilters & { isCsv?: boolean }
    >({
      query: (params) => ({
        url: "admin/reports/subscriptions/discounts",
        method: "GET",
        params: { ...(params || {}), isCsv: true },
        responseType: "blob",
      }),
      transformResponse: (response: unknown): Blob => response as Blob,
      keepUnusedDataFor: 0,
    }),
  }),
});

export const { useGetDiscountReportsQuery, useLazyGetDiscountReportsCsvQuery } =
  DiscountReportsService;
